import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Common/Configurations/AuthContext';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import { Row, Col } from 'react-bootstrap';
import countries from '../../Data/Countries.json';
import currencies from '../../Data/Currency.json';

const EditProfileForm = () => {
    const { user } = useAuth();
    const [companyData, setCompanyData] = useState({});
    const [editedData, setEditedData] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [apiError, setApiError] = useState('');

    useEffect(() => {
        const getData = async () => {
            if (!user?.id) { setIsLoading(false); return; }
            setIsLoading(true);
            try {
                const res = await apiService.getAll(config.GETCOMPANYBYUSER + "/" + user.id);
                setCompanyData(res);
                setEditedData(res);
                setLogoPreview(res.logoUrl);
            } catch (err) {
                setApiError("Failed to fetch profile data.");
                console.error("Error fetching company data", err);
            } finally {
                setIsLoading(false);
            }
        };
        getData();
    }, [user]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedData({ ...editedData, [name]: value });
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const startEdit = () => {
        setEditedData({ ...companyData });
        setEditMode(true);
    };

    const cancelEdit = () => {
        setEditMode(false);
        setEditedData(companyData);
        setLogoFile(null);
        setLogoPreview(companyData.logoUrl);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setApiError('');
        const formData = new FormData();
        formData.append('companyId', user?.cid);
        formData.append('companyName', editedData.companyName);
        formData.append('brandName', editedData.brandName || '');
        formData.append('email', editedData.email);
        formData.append('phoneNumber', editedData.phoneNumber);
        formData.append('gstNo', editedData.gstNo || '');
        formData.append('panCard', editedData.panCard || '');
        formData.append('address', editedData.address || '');
        formData.append('city', editedData.city || '');
        formData.append('state', editedData.state || '');
        formData.append('country', editedData.country);
        formData.append('currency', editedData.currency);
        formData.append("isActive", editedData.isActive);
    formData.append(
      "planType",
      editedData.planType || companyData.planType || ""
    );
        if (logoFile) { formData.append('logoFile', logoFile); }
        try {
            await apiService.update(config.UPDATECOMPANY, formData);
            setEditMode(false);
            const res = await apiService.getAll(config.GETCOMPANYBYUSER + "/" + user.id);
            setCompanyData(res);
            setEditedData(res);
            setLogoFile(null);
            setLogoPreview(res.logoUrl);
        } catch (err) {
            setApiError("Failed to update profile.");
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return <div className="form-loader-overlay"><div className="spinner-border text-primary"></div></div>;
    }
    return (
        <div className="main-content">
            <div className="profile-card-v2 h-100 position-relative card p-2">
                <h4 className="card-title-v2">Agency Profile</h4>
                <form onSubmit={handleUpdate} noValidate>
                    {apiError && <div className="alert alert-danger">{apiError}</div>}
                    <Row className="align-items-start">
                        {/* Logo Section (Left Side) */}
                        <Col md={4} lg={3} className="mb-4">
                            <label className="form-label">Agency Logo</label>
                            <img
                                src={logoPreview || '/assets/images/placeholder-logo.png'}
                                alt="Logo"
                                className="img-thumbnail d-block mb-2 profile-logo-preview"
                            />
                            {editMode && (
                                <input
                                    type="file"
                                    className="form-control form-control-sm"
                                    onChange={handleFileChange}
                                    accept="image/*"
                                />
                            )}
                        </Col>
                        {/* Form Fields Section (Right Side) */}
                        <Col md={8} lg={6}>
                            <Row>
                                <Col md={6} className="mb-3">
                                    <label className="form-label">Agency Name</label>
                                    <input type="text" name="companyName" value={editMode ? editedData.companyName : companyData.companyName} onChange={handleInputChange} className="form-control" readOnly={!editMode} />
                                </Col>
                                <Col md={6} className="mb-3">
                                    <label className="form-label">Brand Name</label>
                                    <input type="text" name="brandName" value={editMode ? editedData.brandName : companyData.brandName} onChange={handleInputChange} className="form-control" readOnly={!editMode} />
                                </Col>
                                <Col md={6} className="mb-3">
                                    <label className="form-label">Email Address</label>
                                    <input type="email" name="email" value={editMode ? editedData.email : companyData.email} onChange={handleInputChange} className="form-control" readOnly={!editMode} />
                                </Col>
                                <Col md={6} className="mb-3">
                                    <label className="form-label">Phone Number</label>
                                    <input type="tel" name="phoneNumber" value={editMode ? editedData.phoneNumber : companyData.phoneNumber} onChange={handleInputChange} className="form-control" readOnly={!editMode} />
                                </Col>
                                <Col md={6} className="mb-3">
                                    <label className="form-label">GST No.</label>
                                    <input type="text" name="gstNo" value={editMode ? editedData.gstNo : companyData.gstNo} onChange={handleInputChange} className="form-control" readOnly={!editMode} />
                                </Col>
                                <Col md={6} className="mb-3">
                                    <label className="form-label">PAN Card</label>
                                    <input type="text" name="panCard" value={editMode ? editedData.panCard : companyData.panCard} onChange={handleInputChange} className="form-control" readOnly={!editMode} />
                                </Col>
                                <Col md={12} className="mb-3">
                                    <label className="form-label">Address</label>
                                    <input type="text" name="address" value={editMode ? editedData.address : companyData.address} onChange={handleInputChange} className="form-control" readOnly={!editMode} />
                                </Col>
                                <Col md={4} className="mb-3">
                                    <label className="form-label">City</label>
                                    <input type="text" name="city" value={editMode ? editedData.city : companyData.city} onChange={handleInputChange} className="form-control" readOnly={!editMode} />
                                </Col>
                                <Col md={4} className="mb-3">
                                    <label className="form-label">State</label>
                                    <input type="text" name="state" value={editMode ? editedData.state : companyData.state} onChange={handleInputChange} className="form-control" readOnly={!editMode} />
                                </Col>
                                <Col md={4} className="mb-3">
                                    <label className="form-label">Country</label>
                                    {editMode ? (
                                        <select name="country" value={editedData.country || ''} onChange={handleInputChange} className="form-select">
                                            {countries.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
                                        </select>
                                    ) : (
                                        <input type="text" value={companyData.country || ''} className="form-control" readOnly />
                                    )}
                                </Col>
                                <Col md={4} className="mb-3">
                                    <label className="form-label">Currency</label>
                                    {editMode ? (
                                        <select name="currency" value={editedData.currency || ''} onChange={handleInputChange} className="form-select">
                                            {currencies.map(c => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
                                        </select>
                                    ) : (
                                        <input type="text" value={companyData.currency || ''} className="form-control" readOnly />
                                    )}
                                </Col>
                            </Row>
                        </Col>
                    </Row>

                    <div className="d-flex justify-content-end align-items-center mt-3 gap-2">
                        {!editMode ? (
                            <button type="button" className="btn btn-primary" onClick={startEdit}>
                                Edit Profile
                            </button>
                        ) : (
                            <>
                                <button type="button" className="btn btn-secondary" onClick={cancelEdit} disabled={isSaving}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-success" disabled={isSaving}>
                                    {isSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditProfileForm;
